import React, { useState, useEffect, useRef } from 'react';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const FloatingConverter = ({ isOpen, onClose, converterType }) => {
  const [amount, setAmount] = useState('');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [result, setResult] = useState('');
  const [gstRate, setGstRate] = useState('18');
  const [gstOperation, setGstOperation] = useState('add');
  const [unitCategory, setUnitCategory] = useState('');
  const [exchangeRates, setExchangeRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const converterRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0 });

  // Unit conversion data
  const unitCategories = {
    length: {
      name: 'Length',
      units: {
        'mm': { name: 'Millimeter', factor: 0.001 },
        'cm': { name: 'Centimeter', factor: 0.01 },
        'm': { name: 'Meter', factor: 1 },
        'km': { name: 'Kilometer', factor: 1000 },
        'in': { name: 'Inch', factor: 0.0254 },
        'ft': { name: 'Foot', factor: 0.3048 },
        'yd': { name: 'Yard', factor: 0.9144 },
        'mi': { name: 'Mile', factor: 1609.34 }
      }
    },
    weight: {
      name: 'Weight',
      units: {
        'mg': { name: 'Milligram', factor: 0.001 },
        'g': { name: 'Gram', factor: 1 },
        'kg': { name: 'Kilogram', factor: 1000 },
        'oz': { name: 'Ounce', factor: 28.3495 },
        'lb': { name: 'Pound', factor: 453.592 },
        'ton': { name: 'Metric Ton', factor: 1000000 }
      }
    },
    volume: {
      name: 'Volume',
      units: {
        'ml': { name: 'Milliliter', factor: 0.001 },
        'l': { name: 'Liter', factor: 1 },
        'gal': { name: 'Gallon (US)', factor: 3.78541 },
        'qt': { name: 'Quart (US)', factor: 0.946353 },
        'pt': { name: 'Pint (US)', factor: 0.473176 },
        'cup': { name: 'Cup (US)', factor: 0.236588 }
      }
    },

    area: {
      name: 'Area',
      units: {
        'mm²': { name: 'Square Millimeter', factor: 0.000001 },
        'cm²': { name: 'Square Centimeter', factor: 0.0001 },
        'm²': { name: 'Square Meter', factor: 1 },
        'km²': { name: 'Square Kilometer', factor: 1000000 },
        'in²': { name: 'Square Inch', factor: 0.00064516 },
        'ft²': { name: 'Square Foot', factor: 0.092903 },
        'yd²': { name: 'Square Yard', factor: 0.836127 },
        'acre': { name: 'Acre', factor: 4046.86 },
        'hectare': { name: 'Hectare', factor: 10000 },
        'mi²': { name: 'Square Mile', factor: 2589988.11 }
      }
    }
  };

  // Currency data
  const currencies = {
    'USD': { name: 'US Dollar', symbol: '$' },
    'EUR': { name: 'Euro', symbol: '€' },
    'GBP': { name: 'British Pound', symbol: '£' },
    'JPY': { name: 'Japanese Yen', symbol: '¥' },
    'INR': { name: 'Indian Rupee', symbol: '₹' },
    'CAD': { name: 'Canadian Dollar', symbol: 'C$' },
    'AUD': { name: 'Australian Dollar', symbol: 'A$' },
    'CHF': { name: 'Swiss Franc', symbol: 'CHF' },
    'CNY': { name: 'Chinese Yuan', symbol: '¥' },
    'AED': { name: 'UAE Dirham', symbol: 'د.إ' },
    'SAR': { name: 'Saudi Riyal', symbol: '﷼' },
    'PKR': { name: 'Pakistani Rupee', symbol: '₨' },
    'BDT': { name: 'Bangladeshi Taka', symbol: '৳' },
    'NPR': { name: 'Nepalese Rupee', symbol: '₨' },
    'LKR': { name: 'Sri Lankan Rupee', symbol: '₨' }
  };

  // Fetch exchange rates
  useEffect(() => {
    if (converterType === 'currency' && isOpen) {
      fetchExchangeRates();
    }
  }, [converterType, isOpen]);

  const fetchExchangeRates = async () => {
    setLoading(true);
    try {
      // Using a free exchange rate API
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      const data = await response.json();
      setExchangeRates(data.rates);
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      // Fallback rates (approximate)
      setExchangeRates({
        USD: 1,
        EUR: 0.85,
        GBP: 0.73,
        JPY: 110,
        INR: 75,
        CAD: 1.25,
        AUD: 1.35,
        CHF: 0.92,
        CNY: 6.45,
        AED: 3.67,
        SAR: 3.75,
        PKR: 160,
        BDT: 85,
        NPR: 120,
        LKR: 200
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle conversion
  useEffect(() => {
    if (converterType === 'gst') {
      if (amount) {
        convert();
      }
    } else {
      if (amount && fromUnit && toUnit) {
        convert();
      }
    }
  }, [amount, fromUnit, toUnit, exchangeRates, gstRate, gstOperation, converterType]);

  const convert = () => {
    if (!amount) return;

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return;

    if (converterType === 'currency') {
      if (!fromUnit || !toUnit) return;
      convertCurrency(numAmount);
    } else if (converterType === 'gst') {
      if (!gstRate) return;
      convertGST(numAmount);
    } else {
      if (!fromUnit || !toUnit) return;
      convertUnit(numAmount);
    }
  };

  const convertCurrency = (numAmount) => {
    if (!exchangeRates[fromUnit] || !exchangeRates[toUnit]) return;

    // Convert to USD first, then to target currency
    const usdAmount = numAmount / exchangeRates[fromUnit];
    const convertedAmount = usdAmount * exchangeRates[toUnit];

    setResult(convertedAmount.toFixed(4));
  };

  const convertGST = (numAmount) => {
    const rate = parseFloat(gstRate);
    if (isNaN(rate) || rate < 0) return;

    const gstAmount = (numAmount * rate) / 100;

    if (gstOperation === 'add') {
      const totalAmount = numAmount + gstAmount;
      setResult({
        gstAmount: gstAmount.toFixed(2),
        totalAmount: totalAmount.toFixed(2),
        operation: 'added'
      });
    } else {
      const originalAmount = numAmount / (1 + rate / 100);
      const gstAmount = numAmount - originalAmount;
      setResult({
        gstAmount: gstAmount.toFixed(2),
        originalAmount: originalAmount.toFixed(2),
        operation: 'subtracted'
      });
    }
  };

  const convertUnit = (numAmount) => {
    const category = Object.keys(unitCategories).find(cat => 
      unitCategories[cat].units[fromUnit] && unitCategories[cat].units[toUnit]
    );

    if (!category) return;

    const fromData = unitCategories[category].units[fromUnit];
    const toData = unitCategories[category].units[toUnit];

         // Standard unit conversion
     const baseAmount = numAmount * fromData.factor;
     const convertedAmount = baseAmount / toData.factor;
     setResult(convertedAmount.toFixed(6));
  };

  // Drag functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    const touch = e.touches[0];
    dragStart.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.current.x,
      y: touch.clientY - dragStart.current.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  if (!isOpen) return null;

  return (
    <div
      ref={converterRef}
      className="fixed bg-white rounded-lg shadow-2xl border border-gray-200 z-50 w-80 max-w-[90vw] sm:w-80"
      style={{
        left: Math.min(Math.max(position.x, 10), window.innerWidth - 330),
        top: Math.min(Math.max(position.y, 10), window.innerHeight - 500)
      }}
    >
      {/* Header - Only this part is draggable */}
      <div
        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg flex justify-between items-center cursor-move touch-none"
        style={{
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div>
          <h3 className="font-bold text-lg">
            {converterType === 'currency' ? 'Currency Converter' :
             converterType === 'gst' ? 'GST Calculator' : 'Unit Converter'}
          </h3>
          <p className="text-blue-100 text-sm">
            {converterType === 'currency' ? 'Live Exchange Rates' :
             converterType === 'gst' ? 'GST Addition/Subtraction' : 'Unit Conversion'}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-blue-200 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {converterType === 'gst' ? (
          <div className="space-y-4">
            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* GST Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GST Rate (%)</label>
              <AppSelect
                value={gstRate}
                onChange={(e) => setGstRate(e.target.value)}
                aria-label="GST Rate"
                sheetTitle="GST Rate"
                options={[
                  { value: '0', label: '0% (Exempt)' },
                  { value: '5', label: '5% (Essential goods)' },
                  { value: '12', label: '12% (Standard rate)' },
                  { value: '18', label: '18% (Most goods & services)' },
                  { value: '28', label: '28% (Luxury items)' },
                ]}
                triggerClassName={APP_SELECT_FILTER_TRIGGER}
              />
            </div>

            {/* Operation Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Operation</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => setGstOperation('add')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    gstOperation === 'add'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Add GST
                </button>
                <button
                  onClick={() => setGstOperation('subtract')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    gstOperation === 'subtract'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Subtract GST
                </button>
              </div>
            </div>

            {/* Result */}
            {result && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                {gstOperation === 'add' ? (
                  <>
                    <div className="text-sm text-green-800 font-medium">GST Calculation:</div>
                    <div className="space-y-1 mt-2">
                      <div className="flex justify-between text-sm">
                        <span>Original Amount:</span>
                        <span>₹{parseFloat(amount).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>GST Amount ({gstRate}%):</span>
                        <span>₹{result.gstAmount}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold text-green-900">
                        <span>Total Amount:</span>
                        <span>₹{result.totalAmount}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm text-green-800 font-medium">GST Extraction:</div>
                    <div className="space-y-1 mt-2">
                      <div className="flex justify-between text-sm">
                        <span>Total Amount:</span>
                        <span>₹{parseFloat(amount).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>GST Amount ({gstRate}%):</span>
                        <span>₹{result.gstAmount}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold text-green-900">
                        <span>Original Amount:</span>
                        <span>₹{result.originalAmount}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ) : converterType === 'currency' ? (
          <div className="space-y-4">
            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* From Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
              <AppSelect
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value)}
                aria-label="From currency"
                sheetTitle="From currency"
                placeholder="Select currency"
                options={[
                  { value: '', label: 'Select currency' },
                  ...Object.entries(currencies).map(([code, data]) => ({
                    value: code,
                    label: `${code} - ${data.name}`,
                  })),
                ]}
                triggerClassName={APP_SELECT_FILTER_TRIGGER}
              />
            </div>

            {/* To Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
              <AppSelect
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value)}
                aria-label="To currency"
                sheetTitle="To currency"
                placeholder="Select currency"
                options={[
                  { value: '', label: 'Select currency' },
                  ...Object.entries(currencies).map(([code, data]) => ({
                    value: code,
                    label: `${code} - ${data.name}`,
                  })),
                ]}
                triggerClassName={APP_SELECT_FILTER_TRIGGER}
              />
            </div>

            {/* Result */}
            {result && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                <div className="text-sm text-green-800 font-medium">Converted Amount:</div>
                <div className="text-lg font-bold text-green-900">
                  {result} {toUnit}
                </div>
                {loading && (
                  <div className="text-xs text-green-600 mt-1">Using live exchange rates</div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <AppSelect
                value={unitCategory}
                onChange={(e) => {
                  setUnitCategory(e.target.value);
                  setFromUnit('');
                  setToUnit('');
                  setResult('');
                }}
                aria-label="Unit category"
                sheetTitle="Category"
                placeholder="Select category"
                options={[
                  { value: '', label: 'Select category' },
                  ...Object.entries(unitCategories).map(([key, data]) => ({
                    value: key,
                    label: data.name,
                  })),
                ]}
                triggerClassName={APP_SELECT_FILTER_TRIGGER}
              />
            </div>

            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

                         {/* From Unit */}
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
               <AppSelect
                 value={fromUnit}
                 onChange={(e) => setFromUnit(e.target.value)}
                 aria-label="From unit"
                 sheetTitle="From unit"
                 placeholder="Select unit"
                 options={[
                   { value: '', label: 'Select unit' },
                   ...Object.entries(unitCategories).flatMap(([, categoryData]) =>
                     Object.entries(categoryData.units).map(([unitKey, unitData]) => ({
                       value: unitKey,
                       label: `${categoryData.name}: ${unitKey} - ${unitData.name} (1 ${unitKey} = ${unitData.factor} base unit)`,
                     }))
                   ),
                 ]}
                 triggerClassName={APP_SELECT_FILTER_TRIGGER}
               />
             </div>

                         {/* To Unit */}
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
               <AppSelect
                 value={toUnit}
                 onChange={(e) => setToUnit(e.target.value)}
                 aria-label="To unit"
                 sheetTitle="To unit"
                 placeholder="Select unit"
                 options={[
                   { value: '', label: 'Select unit' },
                   ...Object.entries(unitCategories).flatMap(([, categoryData]) =>
                     Object.entries(categoryData.units).map(([unitKey, unitData]) => ({
                       value: unitKey,
                       label: `${categoryData.name}: ${unitKey} - ${unitData.name} (1 ${unitKey} = ${unitData.factor} base unit)`,
                     }))
                   ),
                 ]}
                 triggerClassName={APP_SELECT_FILTER_TRIGGER}
               />
             </div>

            {/* Result */}
            {result && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                <div className="text-sm text-green-800 font-medium">Converted Amount:</div>
                <div className="text-lg font-bold text-green-900">
                  {result} {toUnit}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Drag to move</span>
            <span>
              {converterType === 'currency' ? 'Live rates' :
               converterType === 'gst' ? 'GST Calculator' : 'Standard units'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingConverter;
